import React, { useState, useEffect } from 'react';
import { Users, Phone, MapPin, Calendar, User, Search, School, GraduationCap, Shield } from 'lucide-react';
import PickupAPI from '../../firebase/api-pickup';

const TeamsRosters = ({ coachData }) => {
  const [rosters, setRosters] = useState([]);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadRosters();
  }, [coachData]);

  const loadRosters = async () => {
    if (!coachData?.id) return;

    setLoading(true);
    try {
      const data = await PickupAPI.getRostersByCoach(coachData.id);
      setRosters(data);
      if (data.length > 0 && !selectedRoster) {
        setSelectedRoster(data[0]);
      }
    } catch (error) {
      console.error('Error loading rosters:', error);
      setRosters([]);
    } finally {
      setLoading(false);
    }
  };

  const getParticipants = (roster) => {
    return roster.participants || roster.players || [];
  };

  const filteredParticipants = selectedRoster
    ? getParticipants(selectedRoster).filter((p) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const studentName = p.name || `${p.firstName || ''} ${p.lastName || ''}`;
        return (
          studentName.toLowerCase().includes(search) ||
          (p.parentName || '').toLowerCase().includes(search)
        );
      })
    : [];

  const rosterTitle = (roster) =>
    roster.teamName || `${roster.location} / ${roster.sport} / ${roster.grade || roster.ageGroup || ''}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Teams & Rosters</h2>
        <button
          onClick={loadRosters}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Rosters</h3>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading rosters...</p>
              </div>
            ) : rosters.length > 0 ? (
              <div className="space-y-3">
                {rosters.map((roster) => (
                  <button
                    key={roster.id}
                    onClick={() => setSelectedRoster(roster)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedRoster?.id === roster.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                        {roster.sport?.[0] || 'R'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-800 truncate">
                          {rosterTitle(roster)}
                        </h4>
                        <p className="text-sm text-gray-500 truncate">{roster.location}</p>
                        <p className="text-xs text-gray-400">
                          {getParticipants(roster).length} student{getParticipants(roster).length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No rosters assigned yet</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-3">
          {selectedRoster ? (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {rosterTitle(selectedRoster)}
                    </h3>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center">
                        <MapPin size={14} className="mr-1" />
                        {selectedRoster.location}
                      </span>
                      <span className="flex items-center">
                        <Shield size={14} className="mr-1" />
                        {selectedRoster.sport}
                      </span>
                      <span className="flex items-center">
                        <GraduationCap size={14} className="mr-1" />
                        {selectedRoster.grade || selectedRoster.ageGroup || '—'}
                      </span>
                      <span className="flex items-center">
                        <Users size={14} className="mr-1" />
                        {getParticipants(selectedRoster).length} student{getParticipants(selectedRoster).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search students or parents..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {filteredParticipants.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Student</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Parent</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Phone</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredParticipants.map((p, index) => {
                          const studentName = p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown';
                          return (
                            <tr key={p.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
                                    {studentName[0] || '?'}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-800">{studentName}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <p className="text-sm text-gray-600">{p.parentName || '—'}</p>
                              </td>
                              <td className="py-3 px-4">
                                {p.parentPhone ? (
                                  <a
                                    href={`tel:${p.parentPhone}`}
                                    className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                                  >
                                    <Phone size={14} className="mr-1" />
                                    {p.parentPhone}
                                  </a>
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {p.grade || selectedRoster.grade || selectedRoster.ageGroup || '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users size={48} className="mx-auto text-gray-300 mb-3" />
                    <h3 className="text-lg font-medium text-gray-600 mb-2">
                      {getParticipants(selectedRoster).length === 0 ? 'No Students Assigned' : 'No Students Found'}
                    </h3>
                    <p className="text-gray-500">
                      {getParticipants(selectedRoster).length === 0
                        ? "This roster doesn't have any assigned students yet."
                        : 'Try adjusting your search criteria.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <Users size={64} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">Select a Roster</h3>
              <p className="text-gray-500">Choose a roster from the left to view its students and details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamsRosters;
